import { useCallback, type MutableRefObject } from 'react';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import { getLatLongFromGeometry } from './getLatLongFromGeometry';
import type { ArcgisMapView } from './arcgisView';
import type { Geometry } from 'geojson';

const defaultPointSymbol: __esri.SimpleMarkerSymbolProperties & {
  type: 'simple-marker';
} = {
  type: 'simple-marker',
  style: 'circle',
  color: [30, 64, 175, 1],
  size: 12,
  outline: {
    color: [30, 58, 138, 1],
    width: 1.5,
  },
};

const defaultPolygonSymbol: __esri.SimpleFillSymbolProperties & {
  type: 'simple-fill';
} = {
  type: 'simple-fill',
  color: [30, 64, 175, 0.25],
  outline: {
    color: [30, 58, 138, 1],
    width: 2,
  },
};

type UseLocationGraphicLayerArgs = {
  mapView: ArcgisMapView | null;
  drawingLayerRef: MutableRefObject<__esri.GraphicsLayer>;
  clearDrawingLayer: () => void;
  popupTitle?: string;
  pointSymbol?: __esri.SimpleMarkerSymbolProperties & { type: 'simple-marker' };
  polygonSymbol?: __esri.SimpleFillSymbolProperties & { type: 'simple-fill' };
};

export const useLocationGraphicLayer = ({
  mapView,
  drawingLayerRef,
  clearDrawingLayer,
  popupTitle = 'Incident Location',
  pointSymbol = defaultPointSymbol,
  polygonSymbol = defaultPolygonSymbol,
}: UseLocationGraphicLayerArgs) => {
  const drawLocationOnMap = useCallback(
    (
      targetGeometry?: Geometry,
      targetAddress?: string,
      targetMapView: ArcgisMapView | null = mapView
    ) => {
      if (!targetMapView || !targetGeometry) return;

      clearDrawingLayer();

      const coordinates = getLatLongFromGeometry(targetGeometry);
      if (!coordinates) return;

      const geometry = (() => {
        if (targetGeometry.type === 'Point') {
          return new Point({
            longitude: coordinates.longitude,
            latitude: coordinates.latitude,
          });
        } else if (targetGeometry.type === 'MultiPolygon') {
          return new Polygon({ rings: targetGeometry.coordinates.flat() });
        } else if (targetGeometry.type === 'Polygon') {
          return new Polygon({ rings: targetGeometry.coordinates });
        } else {
          throw new Error(`Unsupported geometry type: ${targetGeometry.type}`);
        }
      })();

      const popupTemplate: __esri.PopupTemplateProperties = {
        title: popupTitle,
        content: [
          {
            type: 'fields',
            fieldInfos: [
              {
                fieldName: 'address',
                label: 'Address',
                format: { digitSeparator: false, places: 6 },
              },
              {
                fieldName: 'longitude',
                label: 'Longitude',
                format: { digitSeparator: false, places: 6 },
              },
              {
                fieldName: 'latitude',
                label: 'Latitude',
                format: { digitSeparator: false, places: 6 },
              },
            ],
          },
        ],
      };

      const graphic = new Graphic({
        geometry,
        symbol: (targetGeometry.type === 'Point'
          ? pointSymbol
          : polygonSymbol) as any,
        attributes: {
          longitude: coordinates.longitude,
          latitude: coordinates.latitude,
          address: targetAddress ?? null,
        },
        popupTemplate,
      });

      drawingLayerRef.current.add(graphic);
    },
    [
      clearDrawingLayer,
      drawingLayerRef,
      mapView,
      pointSymbol,
      polygonSymbol,
      popupTitle,
    ]
  );

  return {
    drawLocationOnMap,
  };
};
